// Composables
import { createRouter, createWebHistory } from 'vue-router'
import { useAppStore } from '@/stores/app'

// Sequentially runs return-style guards (Vue Router 4). A guard may return
// `undefined`/`true` to continue, `false` to abort, or a route location to redirect.
const multiguard = (guards) => async (to, from) => {
  for (const guard of guards) {
    const result = await guard(to, from)
    if (result === false) return false
    if (result && typeof result === 'object') return result
  }
}

import {
  viewerMode,
  managerMode,
  logisticsMode,
  deliveryMode,
  showConstructibleList,
  showDesignsList,
  onlyManagersGuard,
  managersOrLogisticsWorkers,
  managersOrDeliveryWorkers,
  // onlyAdminsGuard,
  hasSessionObjectGuard,
  isAuthenticated,
  hasModuleForecast,
  hasModuleProduction,
  hasModuleLogistics,
  hasModuleDelivery,
  hasModuleLite,
  hasModuleMultipanel,
  prodWorkerLogisticsRedirect,
  prevConstHasRCPRunning,
  prevPartBatchRunning,
  asyncStoreDispatcher,
  beforeEachGuard,
}
  from './guards'
import { LEGACY_REDIRECTIONS } from './legacy_redirections'



const routes = [
  // LEGACY REDIRECTIONS
  ...LEGACY_REDIRECTIONS,

  // GENERAL ROUTES
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/misc/Landing.vue'),
    beforeEnter: multiguard([isAuthenticated]),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/misc/Login.vue'),
    props: true,
  },
  {
    // The QR reader is a dialog now (DialogQrReader, mounted in App.vue). The
    // address stays because tablets have it bookmarked: it opens the dialog
    // over the home page.
    path: '/qr',
    name: 'qr',
    redirect: () => {
      useAppStore().qr_reader_open = true
      return { name: 'home' }
    },
  },
  {
    // 'Sign in with Produuz.it' consent screen for external apps (Part B).
    // Handles its own auth redirect, so no isAuthenticated guard here.
    path: '/oauth/authorize',
    name: 'oauth_authorize',
    component: () => import('@/views/misc/OAuthConsent.vue'),
  },
  {
    path: "/account",
    name: "account",
    component: () => import('@/views/misc/AccountView.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, hasModuleProduction])
  },

  // EXTERNAL ROUTES
  {
    path: '/external/:company_uuid/:project_number/:wall_name',
    name: 'external',
    component: () => import('@/views/misc/ExternalURLHandler.vue'),
    beforeEnter: multiguard([isAuthenticated]),
    props: true,
  },






  // VIEWER ROUTES
  {
    path: '/viewer/project/:project_uuid',
    name: 'project_viewer',
    component: () => import('@/views/viewer/ProjectViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, hasSessionObjectGuard, hasModuleProduction, prodWorkerLogisticsRedirect])
  },
  {
    path: '/viewer',
    name: 'viewer',
    component: () => import('@/views/viewer/Viewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, hasSessionObjectGuard, hasModuleProduction, prodWorkerLogisticsRedirect])
  },
  {
    path: '/viewer/assembly_design/:assd_uuid',
    name: 'assd_viewer',
    component: () => import('@/views/viewer/ConstructibleViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, showDesignsList, hasSessionObjectGuard, hasModuleProduction])
  },
  {
    path: '/viewer/assembly/:ass_uuid',
    name: 'ass_viewer',
    meta: { dont_leave_if_rcp_running: true },
    component: () => import('@/views/viewer/ConstructibleViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, showConstructibleList, hasSessionObjectGuard, hasModuleProduction])
  },
  {
    path: '/viewer/module_design/:modd_uuid',
    name: 'modd_viewer',
    component: () => import('@/views/viewer/ConstructibleViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, showDesignsList, hasSessionObjectGuard, hasModuleProduction])
  },
  {
    path: '/viewer/module/:mod_uuid',
    name: 'mod_viewer',
    meta: { dont_leave_if_rcp_running: true },
    component: () => import('@/views/viewer/ConstructibleViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, showConstructibleList, hasSessionObjectGuard, hasModuleProduction])
  },
  {
    path: '/viewer/multipanel_group/:group_uuid',
    name: 'mpgroup_viewer',
    meta: { dont_leave_if_rcp_running: true },
    component: () => import('@/views/viewer/ConstructibleViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, viewerMode, showConstructibleList, hasSessionObjectGuard, hasModuleProduction])
  },
  // PROJECT SPECIFIC ROUTES
  {
    path: '/projects/:project_uuid/constructibles',
    name: 'constructibles',
    component: () => import('@/views/manager/Constructibles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/constructibles/assembly/:ass_uuid',
    name: 'assembly_detail',
    component: () => import('@/views/manager/Constructibles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/constructibles/module/:mod_uuid',
    name: 'module_detail',
    component: () => import('@/views/manager/Constructibles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/parts',
    name: 'project_parts_list',
    component: () => import('@/views/manager/PartsList.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/parts/:part_uuid',
    name: 'project_parts_list_detail',
    component: () => import('@/views/manager/PartsList.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/part_batches',
    name: 'project_part_batches',
    component: () => import('@/views/manager/PartBatches.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    // Same view; the uuid opens the detail drawer, so a batch can be linked to.
    path: '/projects/:project_uuid/part_batches/:part_batch_uuid',
    name: 'project_part_batches_detail',
    component: () => import('@/views/manager/PartBatches.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/files',
    name: 'files',
    component: () => import('@/views/manager/FilesView.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/designs',
    name: 'designs',
    component: () => import('@/views/manager/Designs.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/designs/assembly/:assd_uuid',
    name: 'assembly_design_detail',
    component: () => import('@/views/manager/Designs.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/designs/module/:modd_uuid',
    name: 'module_design_detail',
    component: () => import('@/views/manager/Designs.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/overview',
    name: 'project_overview',
    component: () => import('@/views/manager/ProductionPlanning.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleForecast, managerMode])
  },
  {
    path: '/projects/:project_uuid/issues',
    name: 'issues',
    component: () => import('@/views/manager/Issues.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/issues/:issue_uuid',
    name: 'issue_detail',
    component: () => import('@/views/manager/Issues.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/quantities',
    name: 'quantities',
    component: () => import('@/views/manager/ConstructibleQuantities.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/production_files',
    name: 'production_files',
    component: () => import('@/views/manager/ProductionFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/management_files',
    name: 'management_files',
    component: () => import('@/views/manager/ManagementFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  // A report's drawer opens over its list (docs/report-drawer-proposal.md).
  {
    path: '/projects/:project_uuid/production_files/:report_uuid',
    name: 'production_file_detail',
    component: () => import('@/views/manager/ProductionFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/management_files/:report_uuid',
    name: 'management_file_detail',
    component: () => import('@/views/manager/ManagementFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/projects/:project_uuid/logistics',
    name: 'logistics',
    meta: { dont_leave_if_batch_running: true },
    component: () => import('@/views/logistics/Logistics.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, hasModuleLogistics, managersOrLogisticsWorkers, logisticsMode])
  },
  {
    path: '/projects/:project_uuid/delivery',
    name: 'delivery',
    component: () => import('@/views/manager/Delivery.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleDelivery, managerMode])
  },
  {
    // The company catalogue of rack setups. Creating one used to be possible
    // only from a "+" next to a dropdown, with no way to edit or delete it.
    path: '/company/rack_setups',
    name: 'rack_setups',
    component: () => import('@/views/manager/RackSetups.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleDelivery, managerMode])
  },
  {
    // The delivery mode's landing: "pick a rack load". Same shape as
    // `/viewer` -> Viewer.vue. It carries no project in its path because it
    // serves both the per-project and the company-wide list.
    path: '/delivery',
    name: 'delivery_viewer',
    component: () => import('@/views/delivery/DeliveryViewer.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, managersOrDeliveryWorkers, hasModuleDelivery, deliveryMode])
  },
  {
    // Where the QR on every rack sticker points. The route did not exist, so
    // the catch-all sent every scan to the home page.
    path: '/viewer/rack_loads/:trace_uuid',
    // Stickers printed before the rename carry the old path. They are physical
    // labels on physical racks, so the old address has to keep working.
    alias: '/viewer/rack_traces/:trace_uuid',
    name: 'rack_load',
    component: () => import('@/views/delivery/RackLoadViewer.vue'),
    // `deliveryMode` so a scanned sticker lands *inside* the mode, with the
    // rack list beside it, instead of on an island. Deliberately no module
    // guard and no role guard: a camera is the normal way in, and whoever is
    // standing at the rack has to be able to open what they scanned.
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, deliveryMode])
  },
  {
    path: '/projects/:project_uuid/multipanel',
    name: 'multipanel_groups',
    component: () => import('@/views/manager/MultipanelGroups.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleMultipanel, managerMode])
  },
  {
    path: '/projects/:project_uuid/multipanel/:group_uuid',
    name: 'multipanel_group_detail',
    component: () => import('@/views/manager/MultipanelGroups.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleMultipanel, managerMode])
  },


  // COMPANY WIDE ROUTES
  {
    path: '/company/constructibles_manager',
    name: 'constructibles_manager',
    component: () => import('@/views/manager/ConstructiblesListManager.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/constructibles_manager/assembly/:ass_uuid',
    name: 'constructibles_manager_assembly_detail',
    component: () => import('@/views/manager/ConstructiblesListManager.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/constructibles_manager/module/:mod_uuid',
    name: 'constructibles_manager_module_detail',
    component: () => import('@/views/manager/ConstructiblesListManager.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/constructibles_manager/assembly_design/:assd_uuid',
    name: 'constructibles_manager_assembly_design_detail',
    component: () => import('@/views/manager/ConstructiblesListManager.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/constructibles_manager/module_design/:modd_uuid',
    name: 'constructibles_manager_module_design_detail',
    component: () => import('@/views/manager/ConstructiblesListManager.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/parts',
    name: 'parts_list',
    component: () => import('@/views/manager/PartsList.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/parts/:part_uuid',
    name: 'parts_list_detail',
    component: () => import('@/views/manager/PartsList.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/part_batches',
    name: 'part_batches',
    component: () => import('@/views/manager/PartBatches.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    // Same view; the uuid opens the detail drawer, so a batch can be linked to.
    path: '/company/part_batches/:part_batch_uuid',
    name: 'part_batches_detail',
    component: () => import('@/views/manager/PartBatches.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/issues',
    name: 'company_issues',
    component: () => import('@/views/manager/CompanyIssues.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    // Same view; the uuid opens the issue drawer, so an issue can be linked to.
    path: '/company/issues/:issue_uuid',
    name: 'company_issues_detail',
    component: () => import('@/views/manager/CompanyIssues.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/sizes_and_materials',
    name: 'sizes_and_materials',
    component: () => import('@/views/manager/SizesAndMaterials.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    // Was "Stock products" until the view was renamed. Kept so existing
    // bookmarks and links do not 404.
    path: '/company/stock_products',
    redirect: { name: 'sizes_and_materials' },
  },
  {
    // The link in the "project download ready" message and email.
    path: '/downloads/project_exports/:export_uuid',
    name: 'download_project_export',
    component: () => import('@/views/manager/DownloadProjectExport.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard])
  },
  {
    path: '/company/projects',
    name: 'company_projects_settings',
    component: () => import('@/views/manager/ProjectsSettings.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/production_lines',
    name: 'company_production_lines_settings',
    component: () => import('@/views/manager/ProductionLinesSettings.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/machines',
    name: 'company_machine_settings',
    component: () => import('@/views/manager/MachineSettings.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/automations',
    name: 'company_automations_settings',
    component: () => import('@/views/manager/AutomationsSettings.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/settings',
    name: 'company_settings',
    component: () => import('@/views/manager/Company.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/production_files',
    name: 'company_production_files',
    component: () => import('@/views/manager/ProductionFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/management_files',
    name: 'company_management_files',
    component: () => import('@/views/manager/ManagementFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/production_files/:report_uuid',
    name: 'company_production_file_detail',
    component: () => import('@/views/manager/ProductionFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/management_files/:report_uuid',
    name: 'company_management_file_detail',
    component: () => import('@/views/manager/ManagementFiles.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/file_settings',
    name: 'company_file_settings',
    component: () => import('@/views/manager/FileSettings.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/logistics',
    name: 'company_logistics',
    meta: { dont_leave_if_batch_running: true },
    component: () => import('@/views/logistics/Logistics.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, hasModuleLogistics, managersOrLogisticsWorkers, logisticsMode])
  },
  {
    path: '/company/production_overview',
    name: 'production_overview',
    component: () => import('@/views/manager/ProductionPlanning.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleForecast, managerMode])
  },
  {
    path: '/company/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/Dashboard.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    // Full screen, for a TV: App.vue draws no left menu on it.
    path: '/company/dashboard/screen',
    name: 'dashboard_screen',
    component: () => import('@/views/dashboard/DashboardScreen.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, managerMode])
  },
  {
    path: '/company/tools/moblite',
    name: 'moblite_generator',
    component: () => import('@/views/tools/Moblite.vue'),
    beforeEnter: multiguard([isAuthenticated, hasSessionObjectGuard, onlyManagersGuard, hasModuleLite, managerMode])
  },
  {
    path: "/orders",
    name: 'orders',
    component: () => import('@/views/manager/OrdersView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'redirect',
    redirect: '/'
  },
  // {
  //   path: '/about',
  //   name: 'about',
  //   // route level code-splitting
  //   // this generates a separate chunk (about.[hash].js) for this route
  //   // which is lazy-loaded when the route is visited.
  //   component: () => import(/* webpackChunkName: "about" */ '../views/About.vue')
  // }
]


const router = createRouter({
  // history: createWebHistory(process.env.BASE_URL),
  history: createWebHistory('/app/'),
  routes,
})



router.beforeEach(prevConstHasRCPRunning)
router.beforeEach(prevPartBatchRunning)
router.beforeEach(asyncStoreDispatcher)
router.beforeEach(beforeEachGuard)

// A guard error aborts the navigation and skips the remaining guards; clear
// the route loader so the app-wide overlay can't get stuck.
router.onError(() => useAppStore().popLoading("route"))


export default router
